"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { LeadPopupAutoOpen } from "@/components/shared/LeadPopup";
import { SidebarBookingForm } from "@/components/shared/SidebarBookingForm";
import { CtaBand } from "@/components/home/CtaBand";
import { Doctors } from "@/components/home/Doctors";
import { GoogleReviews } from "@/components/home/GoogleReviews";
import { WhyDermalife } from "@/components/home/WhyDermalife";
import { Awards } from "@/components/home/Awards";
import { SafeHtml } from "@/components/shared/SafeHtml";
import { ProcedureSectionRenderer } from "@/components/procedure-sections/ProcedureSectionRenderer";
import type { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { TrustStrip } from "@/components/home/TrustStrip";
import { FaqSection } from "@/components/ui/FaqSection";
import { publicUrl } from "@/lib/content/path";
import { medicalPageJsonLd } from "@/lib/seo";
import type { PublicContent } from "@/lib/content/public";

type Gallery = { id: string; title?: string | null; image?: string | null; beforeImage?: string | null; afterImage?: string | null; altText?: string | null };
type HairCaseSummary = {
  id: string;
  title: string;
  slug: string;
  image?: string | null;
  alt?: string | null;
  month?: string | null;
  grade?: string | null;
  grafts?: string | null;
  method?: string | null;
  category?: { title: string; slug: string } | null;
};
type HairCaseCategorySummary = { title: string; slug: string };
type RelatedHairCase = { title?: string | null; image?: string | null; alt?: string | null; href?: string | null };
type BreadcrumbItem = { label: string; href?: string };
type ProcedurePreviewImage = { image?: string | null; alt?: string | null; title?: string | null };
type ProcedureFaq = { id: string; question: string; answer: string };

function ColoredText({ text, baseColor = "text-brand-black" }: { text: string; baseColor?: string }) {
  const words = text.split(" ");
  if (words.length <= 1) {
    return <span className={baseColor}>{text}</span>;
  }
  let orangeWordCount = 1;
  if (words.length >= 6) {
    orangeWordCount = 3;
  } else if (words.length >= 4) {
    orangeWordCount = 2;
  }
  const firstPart = words.slice(0, words.length - orangeWordCount).join(" ");
  const lastPart = words.slice(words.length - orangeWordCount).join(" ");
  return (
    <>
      <span className={baseColor}>{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}


function ContentGallery({ items }: { items: Gallery[] }) {
  if (!items.length) return null;
  return (
    <section className="mt-12">
      <h2 className="text-3xl font-semibold text-brand-black">
        <ColoredText text="Treatment results" baseColor="text-brand-black" />
      </h2>
      <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          const source = item.afterImage || item.image || item.beforeImage;
          if (!source) return null;
          return (
            <figure key={item.id} className="overflow-hidden rounded-3xl border border-brand-gray-light bg-white shadow-sm">
              <div className="w-full overflow-hidden bg-brand-gray-light">
                <img 
                  src={publicUrl(source)} 
                  alt={item.altText || item.title || "Dermalife treatment result"} 
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-105" 
                />
              </div>
              {item.title && <figcaption className="p-4 text-sm font-semibold text-brand-black">{item.title}</figcaption>}
            </figure>
          );
        })}
      </div>
    </section>
  );
}

function EmptyResultsNotice({ title }: { title: string }) {
  return (
    <section className="mt-12 overflow-hidden rounded-3xl border border-brand-gray-light bg-gradient-to-br from-brand-peach/20 via-white to-brand-green/10 p-8">
      <p className="font-accent text-xs uppercase text-brand-orange-deep">Results gallery</p>
      <h2 className="mt-4 text-3xl font-semibold text-brand-black">
        <ColoredText text={`${title} results`} baseColor="text-brand-black" />
      </h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-brand-gray">
        The legacy website did not have published result images attached to this
        category. This page is live with its preserved URL and enquiry flow; result
        images can be added from the admin panel when available.
      </p>
      <Link
        href="/contact-us"
        data-lead-popup
        className="mt-6 inline-flex rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange-deep hover:shadow-md"
      >
        Book Appointment
      </Link>
    </section>
  );
}

function normalizeResultType(value: string | null | undefined) {
  return value?.trim().toLowerCase().replace(/\s+/g, "-").replace(/\//g, "").replace(/-+/g, "-") ?? "";
}

function HairCaseExplorer({
  items,
  categories,
  selectedCategory,
}: {
  items: HairCaseSummary[];
  categories: HairCaseCategorySummary[];
  selectedCategory?: HairCaseCategorySummary | null;
}) {
  if (!categories.length) return null;
  const activeType = normalizeResultType(selectedCategory?.slug || selectedCategory?.title || "all");
  return (
    <section className="mt-4 scroll-mt-24" id="hair-cases-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-brand-gray-light pb-6">
        <div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-brand-black tracking-tight">
            <ColoredText text="Hair Transplant Results Gallery" baseColor="text-brand-black" />
          </h2>
        </div>
        <p className="mt-3 md:mt-0 text-sm text-brand-gray font-medium">
          Showing <span className="text-brand-orange font-semibold">{items.length}</span> patient cases
        </p>
      </div>

      {/* Simple Navigation */}
      <nav aria-label="Filter results" className="mt-6 flex flex-wrap gap-2">
        {categories.map((category) => {
          const active = [category.slug, category.title].map(normalizeResultType).includes(activeType);
          return (
            <Link
              key={category.slug}
              href={`/results/male-hair-transplant?type=${encodeURIComponent(category.title)}`}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                active 
                  ? "bg-brand-orange text-white shadow-sm" 
                  : "bg-brand-gray-light text-brand-black/75 hover:bg-brand-peach/25 hover:text-brand-orange"
              }`}
            >
              {category.title}
            </Link>
          );
        })}
      </nav>

      {items.length ? (
        <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Link 
              key={item.id} 
              href={`/results/male-hair-transplant/${item.category?.slug || "case"}/${item.slug}`} 
              className="group overflow-hidden rounded-3xl border border-brand-gray-light bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
            >
              {item.image ? (
                <div className="w-full overflow-hidden bg-brand-gray-light">
                  <img 
                    src={publicUrl(item.image)} 
                    alt={item.alt || item.title} 
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-105" 
                  />
                </div>
              ) : null}
              <div className="p-5">
                <h3 className="font-semibold text-brand-black group-hover:text-brand-orange-deep">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs text-brand-gray">
                  {[item.grade ? `Grade ${item.grade}` : "", item.grafts ? `${item.grafts} Grafts` : "", item.method].filter(Boolean).join(" · ")}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-7 rounded-2xl bg-brand-gray-light p-5 text-sm text-brand-gray">
          No cases match this filter.
        </p>
      )}
    </section>
  );
}

function resultAfterText(value: unknown) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return /\bmonths?\b/i.test(text) ? `Result after: ${text}` : `Result after: ${text} months`;
}

function breadcrumbsFor(content: PublicContent): BreadcrumbItem[] {
  if (content.type === "result-category") {
    return [
      { label: "Home", href: "/" },
      { label: "Result" },
      { label: content.title },
    ];
  }

  if (content.type === "hair-case") {
    const category = String(content.data?.category || "");
    const categorySlug = String(content.data?.categorySlug || "");
    return [
      { label: "Home", href: "/" },
      { label: "Result" },
      { label: "Male Hair Transplant", href: "/results/male-hair-transplant" },
      ...(category ? [{ label: category, href: categorySlug ? `/results/male-hair-transplant/${categorySlug}` : undefined }] : []),
      { label: content.title },
    ];
  }

  return [
    { label: "Home", href: "/" },
    { label: content.title },
  ];
}
import { ClientHeroVideo } from "@/components/shared/ClientHeroVideo";

function PageHero({ content }: { content: PublicContent }) {
  const crumbs = breadcrumbsFor(content);

  return (
    <section className="relative isolate flex min-h-[600px] items-center overflow-hidden bg-brand-black pt-32 pb-16 text-white lg:min-h-[760px] lg:pt-44 lg:pb-20">
      <ClientHeroVideo />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-brand-black/88 via-brand-black/45 to-brand-black/10" />
      <div className="absolute inset-y-0 left-0 -z-10 w-full max-w-[900px] bg-gradient-to-r from-brand-black/75 via-brand-black/35 to-transparent" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-brand-black/45 via-transparent to-transparent" />
      <div className="absolute inset-0 -z-10 brand-cell-pattern opacity-[0.06]" />
      <div className="absolute -right-24 -top-32 -z-10 h-96 w-96 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="relative flex w-full items-center px-6 md:px-12 xl:px-[170px]">
        <div className="max-w-2xl">
          <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-sm text-white/55">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`} className="inline-flex items-center gap-2">
                {index > 0 && <span>/</span>}
                {crumb.href ? <Link href={crumb.href} className="hover:text-white">{crumb.label}</Link> : <span className="text-brand-peach">{crumb.label}</span>}
              </span>
            ))}
          </nav>
          <p className="font-accent text-xs uppercase text-brand-peach">Dermalife Skin & Hair Clinic</p>
          <h1 className="mt-4 text-4xl font-semibold leading-[1.1] text-white sm:text-5xl lg:text-6xl">
            <ColoredText text={content.title} baseColor="text-white" />
          </h1>
          {content.excerpt && <p className="mt-6 max-w-lg text-base leading-relaxed text-white/75 sm:text-lg">{content.excerpt}</p>}
        </div>
      </div>
    </section>
  );
}

function HairCaseSideRail({
  relatedCase,
  videoEmbedUrl,
}: {
  relatedCase?: RelatedHairCase | null;
  videoEmbedUrl?: string | null;
}) {
  if (!relatedCase?.image && !videoEmbedUrl) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="inline-flex bg-[#0b2d45] px-5 py-2 text-xl font-medium text-white">
          Before &amp; After
        </h2>
        {relatedCase?.image && (
          <Link
            href={publicUrl(relatedCase.href || "#")}
            className="mt-5 block overflow-hidden rounded-2xl bg-brand-gray-light shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="relative aspect-[3/2]">
              <Image
                src={publicUrl(relatedCase.image)}
                alt={relatedCase.alt || relatedCase.title || "Before and after hair transplant result"}
                fill
                unoptimized
                className="object-contain"
              />
            </div>
            {relatedCase.title && <p className="px-4 py-3 text-center text-sm font-semibold text-brand-black">{relatedCase.title}</p>}
          </Link>
        )}
      </div>

      {videoEmbedUrl && (
        <div className="overflow-hidden rounded-2xl bg-brand-black shadow-sm">
          <iframe
            title="Dermalife hair transplant patient journey"
            src={videoEmbedUrl}
            className="aspect-video w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}

function extractProcedureContent(html?: string | null): { headingText: string | null; bodyHtml: string } {
  const content = String(html ?? "").trimStart();
  if (!content) return { headingText: null, bodyHtml: "" };

  if (!content.startsWith("<")) {
    const match = content.match(/^([^\r\n<][^\r\n]*)(\r?\n\s*\r?\n)(?=<)/);
    if (match) {
      return { headingText: match[1].trim(), bodyHtml: content.substring(match[0].length) };
    }
  } else {
    const h1Match = content.match(/^(<h1[^>]*>)(.*?)(<\/h1>)(?:\r?\n\s*)*/i);
    if (h1Match) {
      const text = h1Match[2].replace(/<[^>]*>?/gm, '').trim();
      return { headingText: text, bodyHtml: content.substring(h1Match[0].length) };
    }
  }

  return { headingText: null, bodyHtml: content };
}

const isDropdownPageSlug = (slug: string) => {
  const clean = slug.replace(/^\//, "").replace(/\.php$/, "");
  return [
    "know-your-hair",
    "dandruff-treatment",
    "hair-loss-treatment",
    "hair-re-growth-treatment",
    "hair-treatment/mesotherapy",
    "hair-treatment/lllt",
    "scalp-micro-pigmentation",
    "hair-treatment/hair-root",
    "success-stories",
    "global-client",
    "faq",
    "hair-transplant",
    "hair-transplant/basics-of-hair-transplant",
    "hair-transplant-cost-in-delhi",
    "hair-transplant/natural-hairline-transplant",
    "hair-transplant/whorl-pattern-area-transplant",
    "boosted-fue-hair-transplant",
    "fue-hair-transplant",
    "fut-hair-transplant",
    "hair-transplant/robotic-hair-transplant",
    "hair-transplant/boosted-fue-vs-fut",
    "hair-transplant/repair-hair-transplant",
    "hair-transplant/beard-hair-transplant",
    "eyebrow-hair-transplant-new",
    "hair-transplant/rejuvenation-medicine-hair-transplant",
    "long-hair-transplant",
    // Skin dropdown pages
    "Skin/disease-and-problem",
    "acne-treatment",
    "melasma",
    "dark-circles",
    "moles",
    "warts",
    "vitiligo",
    "eczema",
    "psoriasis-treatment",
    "keloids",
    "nail-problems",
    "full-body-skin-lightening-surgery",
    // Cosmetology & Skin Care dropdown pages
    "anti-wrinkle-injection-fillers",
    "hydrafacial-treatment-in-delhi",
    "chemical-peels-treatment",
    "fillers",
    "thread-lift",
    // Laser & Light dropdown pages
    "stretch-mark-treatment",
    "carbon-laser-peel",
    "laser/laser-hair-removal",
    "acne-scar-reduction-treatment",
    "dark-spots-removal-treatment",
    "freckle-removal-treatment",
    "melasma-reduction",
    "open-pores-treatment",
    "laser/tattoo-removal-in-delhi",
    "mole-removal"
  ].includes(clean);
};
const CONSULTATION_BUTTON_EXACT_SLUGS = new Set([
  // Hair Transplant
  "hair-transplant",
  "hair-transplant/basics-of-hair-transplant",
  "hair-transplant-cost-in-delhi",
  "hair-transplant/natural-hairline-transplant",
  "hair-transplant/whorl-pattern-area-transplant",
  "boosted-fue-hair-transplant",
  "fue-hair-transplant",
  "fut-hair-transplant",
  "hair-transplant/robotic-hair-transplant",
  "hair-transplant/boosted-fue-vs-fut",
  "hair-transplant/repair-hair-transplant",
  "hair-transplant/beard-hair-transplant",
  "eyebrow-hair-transplant-new",
  "eyebrow-hair-transplant",
  "hair-transplant/rejuvenation-medicine-hair-transplant",
  "long-hair-transplant",
  // Skin
  "skin/disease-and-problem",
  "disease-and-problem",
  "acne-treatment",
  "melasma",
  "dark-circles",
  "moles",
  "warts",
  "vitiligo",
  "eczema",
  "psoriasis-treatment",
  "keloids",
  "nail-problems",
  "full-body-skin-lightening-surgery",
  // Cosmetology & Skin Care
  "anti-wrinkle-injection-fillers",
  "hydrafacial-treatment-in-delhi",
  "chemical-peels-treatment",
  "fillers",
  "thread-lift",
  // Laser & Light
  "stretch-mark-treatment",
  "carbon-laser-peel",
  "laser/laser-hair-removal",
  "laser-hair-removal",
  "acne-scar-reduction-treatment",
  "dark-spots-removal-treatment",
  "freckle-removal-treatment",
  "melasma-reduction",
  "open-pores-treatment",
  "laser/tattoo-removal-in-delhi",
  "tattoo-removal-in-delhi",
  "mole-removal",
  // Shared Dropdown Pages
  "success-stories",
  "global-client",
  "faq",
  "hair-transplant-faqs",
]);

function shouldShowConsultationButton(slug?: string | null, canonicalPath?: string | null): boolean {
  const cleanSlug = (slug || "").replace(/^\//, "").replace(/\/$/, "").replace(/\.php$/, "").toLowerCase();
  const cleanPath = (canonicalPath || "").replace(/^\//, "").replace(/\/$/, "").replace(/\.php$/, "").toLowerCase();

  if (CONSULTATION_BUTTON_EXACT_SLUGS.has(cleanSlug) || CONSULTATION_BUTTON_EXACT_SLUGS.has(cleanPath)) {
    return true;
  }

  if (
    cleanSlug.startsWith("hair-transplant/") ||
    cleanPath.startsWith("hair-transplant/") ||
    cleanSlug.startsWith("skin/") ||
    cleanPath.startsWith("skin/") ||
    cleanSlug.startsWith("laser/") ||
    cleanPath.startsWith("laser/") ||
    cleanSlug.includes("hair-transplant") ||
    cleanPath.includes("hair-transplant")
  ) {
    return true;
  }

  return false;
}

function LegacyProcedureContent({ content, canonicalPath }: { content: PublicContent; canonicalPath?: string }) {
  const isConsultationPage = shouldShowConsultationButton(content.slug, canonicalPath);
  const previewImages = (content.data?.previewImages as ProcedurePreviewImage[] | undefined) ?? [];
  const previewResultsHref = String(content.data?.previewResultsHref || "/results/male-hair-transplant");
  const faqs = (content.data?.faqs as ProcedureFaq[] | undefined) ?? [];
  const rawSections = content.data?.sections;
  const structuredSections: ProcedureSectionData[] = Array.isArray(rawSections)
    ? (rawSections as ProcedureSectionData[])
    : typeof rawSections === "string"
    ? (() => {
        try {
          const parsed = JSON.parse(rawSections);
          return Array.isArray(parsed) ? (parsed as ProcedureSectionData[]) : [];
        } catch {
          return [];
        }
      })()
    : [];
  const hasStructuredSections = structuredSections.length > 0;
  const { headingText, bodyHtml } = extractProcedureContent(content.body);

  return (
    <>
      {/* 1. Case Studies / Before & After Section (Immediately After Hero Banner) */}
      {previewImages.length > 0 && (
        <section className="w-full bg-white px-6 py-6 md:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/60">
          <div>
            <div className="text-center mb-10">
              <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                Real Patient Transformations
              </span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-brand-black">
                Before &amp; After <span className="text-brand-orange">Clinical Results</span>
              </h2>
            </div>

            <div className={`grid gap-6 ${
              previewImages.slice(0, 3).length === 1 
                ? "grid-cols-1 max-w-md mx-auto w-full" 
                : previewImages.slice(0, 3).length === 2 
                  ? "grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto w-full" 
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            }`}>
              {previewImages.slice(0, 3).map((item, index) => (
                item.image && (
                  <Link 
                    key={`${item.image}-${index}`} 
                    href={publicUrl(item.image)}
                    target="_blank"
                    className="group relative flex flex-col overflow-hidden rounded-3xl border border-brand-gray/15 bg-white shadow-sm ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(229,110,62,0.15)]"
                  >
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-brand-gray-light">
                      <Image
                        src={publicUrl(item.image)}
                        alt={item.alt || item.title || `${content.title} result ${index + 1}`}
                        fill
                        unoptimized
                        className="object-cover transition-transform duration-700 group-hover:scale-105 group-hover:opacity-95"
                      />
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-brand-black/0 group-hover:bg-brand-black/10 transition-colors duration-500 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-brand-orange shadow-lg backdrop-blur-sm transform scale-50 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-500 delay-75">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                href={previewResultsHref}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange-deep hover:shadow-md"
              >
                <span>View More Results</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 2. Top Intro Section with Sticky Booking Form (Aligned with Header: px-6 md:px-12 xl:px-[170px]) */}
      <section className="w-full bg-white px-6 py-6 md:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/40">
        <div className="grid w-full grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_400px]">
          {/* Left Intro Content */}
          <div className="min-w-0 flex flex-col">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-black mb-6 leading-tight">
              <ColoredText text={headingText || content.title} baseColor="text-brand-black" />
            </h2>

            {content.image && (
              <div className="mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px]">
                <Image
                  src={publicUrl(content.image)}
                  alt={content.title}
                  width={1200}
                  height={800}
                  unoptimized
                  priority
                  className="w-full h-auto max-h-[500px] object-contain"
                />
              </div>
            )}
          </div>

          {/* Right Sidebar Booking Form */}
          <aside className="min-w-0">
            <div className="sticky top-32">
              <SidebarBookingForm
                buttonText={isConsultationPage ? "Get Free Hair Transplant consultation" : undefined}
              />
            </div>
          </aside>
        </div>

        <div className="mt-12 lg:mt-16 w-full min-w-0 flex flex-col">
          <div className="w-24 h-1 bg-brand-orange rounded-full mt-2 mb-8" />
          <SafeHtml html={bodyHtml} className="cms-content legacy-procedure-content" />
        </div>
      </section>

      {/* 3. Structured Modern Sections (When configured in Admin) */}
      {hasStructuredSections && (
        <ProcedureSectionRenderer sections={structuredSections} />
      )}

      {/* 4. Doctors Spotlight */}
      <Doctors />

      {/* 5. Google Reviews */}
      <GoogleReviews />

      {/* 6. Why Dermalife */}
      <WhyDermalife />

      {/* 7. Dynamic Procedure FAQs */}
      {faqs.length > 0 && (
        <FaqSection
          faqs={faqs}
          title="Frequently Asked Questions"
          subtitle=""
          className="bg-white pb-16"
        />
      )}

      {/* 8. CTA Band */}
      <CtaBand />
    </>
  );
}

function LegacyFaqContent({ content }: { content: PublicContent }) {
  const faqs = (content.data?.faqs as ProcedureFaq[] | undefined) ?? [];

  return (
    <>
      <section className="w-full bg-white px-6 py-6 md:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/40">
        <div className="grid w-full grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_400px]">
          <article className="min-w-0">
            {content.image && (
              <div className="mb-10 overflow-hidden rounded-3xl bg-brand-gray-light">
                <Image
                  src={publicUrl(content.image)}
                  alt={content.title}
                  width={1400}
                  height={900}
                  unoptimized
                  priority
                  className="aspect-[1920/938] w-full object-contain bg-brand-gray-light"
                />
              </div>
            )}
            {faqs.length > 0 ? (
              <FaqSection
                faqs={faqs}
                title="Frequently Asked Questions"
                subtitle=""
                className="bg-white"
              />
            ) : (
              <>
                <h1 className="text-4xl font-semibold text-brand-black">Frequently Asked Questions</h1>
                <p className="mt-5 rounded-2xl bg-brand-gray-light p-5 text-sm text-brand-gray">FAQs are being updated.</p>
              </>
            )}
          </article>
          <aside className="min-w-0">
            <div className="sticky top-32">
              <SidebarBookingForm buttonText="Get Free Hair Transplant consultation" />
            </div>
          </aside>
        </div>
      </section>

      {isDropdownPageSlug(content.slug) && (
        <>
          <CtaBand />
          <Doctors />
        </>
      )}
    </>
  );
}

function VideoLightbox({ videoId, onClose }: { videoId: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-brand-black/85 px-5 backdrop-blur-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-black"
      >
        Close
      </button>
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-brand-black shadow-2xl">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title="Dermalife video"
          className="aspect-video w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

type SuccessStoryItem = {
  id: string;
  image?: string | null;
  video?: string | null;
  description?: string | null;
};

function LegacySuccessStoriesContent({ content }: { content: PublicContent }) {
  const patients = (content.data?.patients as SuccessStoryItem[] | undefined) ?? [];
  const bannerImage = content.data?.legacyBanner as string | null | undefined;
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  return (
    <>
      <section className="w-full bg-white px-6 py-6 md:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/40">
        <div className="grid w-full grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_400px]">
          <article className="min-w-0 space-y-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-brand-black">
              <ColoredText text="Success Stories of Dermalife" baseColor="text-brand-black" />
            </h1>

            {bannerImage && (
              <div className="overflow-hidden rounded-3xl bg-brand-gray-light shadow-sm">
                <Image
                  src={publicUrl(bannerImage)}
                  alt="Dermalife Success Stories"
                  width={1400}
                  height={900}
                  unoptimized
                  priority
                  className="w-full h-auto object-cover bg-brand-gray-light"
                />
              </div>
            )}

          </article>
          <aside className="min-w-0">
            <div className="sticky top-32">
              <SidebarBookingForm buttonText="Get Free Hair Transplant consultation" />
            </div>
          </aside>
        </div>

        <div className="w-full mt-16 lg:mt-20">
          <div className="space-y-16">
            {patients.map((patient, index) => {
              const cleanVideoId = String(patient.video || "").split("&")[0];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={patient.id}
                  className={`flex flex-col gap-6 border-b border-brand-gray-light pb-12 last:border-0 md:flex-row ${isEven ? "md:flex-row" : "md:flex-row-reverse"
                    } items-start`}
                >
                  {/* Video Thumbnail Section */}
                  {patient.image && (
                    <div className="w-full md:w-[320px] lg:w-[360px] shrink-0">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => cleanVideoId && setActiveVideoId(cleanVideoId)}
                        onKeyDown={(e) => {
                          if ((e.key === "Enter" || e.key === " ") && cleanVideoId) {
                            setActiveVideoId(cleanVideoId);
                          }
                        }}
                        className="group relative block aspect-video w-full overflow-hidden rounded-2xl bg-brand-gray-light shadow-md transition hover:-translate-y-1 hover:shadow-lg border-[3px] border-white cursor-pointer"
                      >
                        <Image
                          src={publicUrl(patient.image)}
                          alt={`Dermalife Patient Success Story ${index + 1}`}
                          fill
                          unoptimized
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg transition-transform group-hover:scale-110">
                            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="flex-1 space-y-4">
                    {patient.description && (
                      <SafeHtml
                        html={patient.description}
                        className="cms-content text-justify text-brand-gray leading-relaxed"
                      />
                    )}
                    <div className="pt-2">
                      <Link
                        href="/contact-us"
                        data-lead-popup
                        className="inline-flex rounded-full bg-brand-orange px-6 py-3 text-xs font-semibold text-white shadow-sm transition-all hover:bg-brand-orange-deep hover:shadow-md"
                      >
                        Book Your Appointment &rarr;
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {isDropdownPageSlug(content.slug) && (
        <>
          <CtaBand />
          <Doctors />
        </>
      )}

      {activeVideoId && (
        <VideoLightbox videoId={activeVideoId} onClose={() => setActiveVideoId(null)} />
      )}
    </>
  );
}

export function LegacyContentPage({ content, canonicalPath }: { content: PublicContent; canonicalPath: string }) {
  const galleries = (content.data?.galleries as Gallery[] | undefined) ?? [];
  const hairCases = (content.data?.hairCases as HairCaseSummary[] | undefined) ?? [];
  const hairCaseCategories = (content.data?.hairCaseCategories as HairCaseCategorySummary[] | undefined) ?? [];
  const selectedHairCaseCategory = content.data?.selectedHairCaseCategory as HairCaseCategorySummary | undefined;
  const caseImages = (content.data?.images as string[] | undefined) ?? [];
  const caseAlts = (content.data?.alts as Array<string | null> | undefined) ?? [];
  const relatedHairCase = content.data?.relatedHairCase as RelatedHairCase | null | undefined;
  const legacyVideoEmbedUrl = content.data?.legacyVideoEmbedUrl as string | null | undefined;
  const isMaleHairTransplantResults = content.slug === "male-hair-transplant" || content.slug === "results/male-hair-transplant";
  const showEmptyResultsNotice = content.type === "result-category" && !galleries.length && (!isMaleHairTransplantResults || !hairCases.length);
  const jsonLd = medicalPageJsonLd(content, canonicalPath);

  return (
    <>
      {content.type === "city-procedure" && <LeadPopupAutoOpen />}
      <Header />
      <main className="flex-1 bg-white">
        <PageHero content={content} />

        {content.slug === "faq.php" || content.slug === "hair-transplant-faqs" || content.slug === "faq" ? (
          <LegacyFaqContent content={content} />
        ) : content.slug === "success-stories.php" ? (
          <LegacySuccessStoriesContent content={content} />
        ) : (content.type === "procedure" || content.type === "city-procedure") ? (
          <LegacyProcedureContent content={content} canonicalPath={canonicalPath} />
        ) : (
          (() => {
            const { headingText, bodyHtml } = extractProcedureContent(content.body);
            return (
              <>
                <section className="w-full bg-white px-6 py-6 md:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/40">
                  <div className="grid w-full grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_400px]">
                    <div className="min-w-0 flex flex-col">
                      <h2 className="text-3xl sm:text-4xl font-bold text-brand-black mb-6 leading-tight">
                        <ColoredText text={headingText || content.title} baseColor="text-brand-black" />
                      </h2>

                      {content.image && (
                        <div className="mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px]">
                          <Image
                            src={publicUrl(content.image)}
                            alt={content.title}
                            width={1200}
                            height={800}
                            unoptimized
                            priority
                            className="w-full h-auto max-h-[500px] object-contain"
                          />
                        </div>
                      )}

                      {content.type === "hair-case" && (
                        <div className="mb-8 grid grid-cols-2 gap-4 rounded-3xl bg-brand-peach/20 p-6 sm:grid-cols-4">
                          {([
                            resultAfterText(content.data?.month),
                            content.data?.grade ? `Grade of Baldness: ${content.data.grade}` : "",
                            content.data?.grafts ? `Grafts Implanted: ${content.data.grafts}` : "",
                            content.data?.method ? `Method: ${content.data.method}` : "",
                          ] as string[]).filter(Boolean).map((text) => (
                            <div key={text} className="rounded-2xl bg-brand-green-deep px-4 py-3 text-center">
                              <p className="text-sm font-semibold text-white">{text}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <SafeHtml html={bodyHtml} className="cms-content text-brand-gray" />

                      {caseImages.length > 0 && (
                        <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2">
                          {caseImages.map((source, index) => (
                            <div key={`${source}-${index}`} className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-brand-gray-light">
                              <Image src={publicUrl(source)} alt={caseAlts[index] || `${content.title} view ${index + 1}`} fill unoptimized className="object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <aside className="min-w-0">
                      <div className="sticky top-32 space-y-8">
                        {content.type === "hair-case" && <HairCaseSideRail relatedCase={relatedHairCase} videoEmbedUrl={legacyVideoEmbedUrl} />}
                        <SidebarBookingForm
                          buttonText={
                            shouldShowConsultationButton(content.slug, canonicalPath)
                              ? "Get Free Hair Transplant consultation"
                              : undefined
                          }
                        />
                      </div>
                    </aside>
                  </div>
                </section>

                <div className="w-full px-6 md:px-12 xl:px-[170px] mt-12 lg:mt-16">
                  <ContentGallery items={galleries} />
                  {isMaleHairTransplantResults && <HairCaseExplorer items={hairCases} categories={hairCaseCategories} selectedCategory={selectedHairCaseCategory} />}
                  {showEmptyResultsNotice && <EmptyResultsNotice title={content.title} />}
                </div>

                {content.type === "result-category" && (
                  <>
                    <WhyDermalife />
                    <GoogleReviews />
                    <CtaBand />
                    <Doctors />
                    <Awards />
                  </>
                )}
                {isDropdownPageSlug(content.slug) && content.type !== "result-category" && (
                  <>
                    <CtaBand />
                    <Doctors />
                  </>
                )}
              </>
            );
          })()
        )}

      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replaceAll("<", "\\u003c") }} />
    </>
  );
}
